import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Paper,
  Button,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  Grid,
  TextField,
  InputAdornment,
  IconButton,
  Alert,
  Snackbar,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
} from '@mui/material';
import { DataGrid } from '@mui/x-data-grid';
import SearchIcon from '@mui/icons-material/Search';
import ClearIcon from '@mui/icons-material/Clear';
import PinDropIcon from '@mui/icons-material/PinDrop';
import LocationCityIcon from '@mui/icons-material/LocationCity';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import DownloadIcon from '@mui/icons-material/Download';
import axios from 'axios';
import { API_BASE_URL } from '../../../constants/constants';
import { jwtDecode } from 'jwt-decode';
import { usePermissions } from '../../../context/PermissionContext';
import BackButton from '../../../components/BackButton';
import { LEVEL_NAMES } from '../../../constants/userLevels';

const QPayCentresList = () => {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [userLevel, setUserLevel] = useState(null);
  const { can } = usePermissions();
  const [userId, setUserId] = useState(null);
  const [searchText, setSearchText] = useState('');
  const [filterStatus, setFilterStatus] = useState('');

  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState('');
  const [snackbarSeverity, setSnackbarSeverity] = useState('info');

  // Confirmation Modal State for Status Toggle
  const [confirmModalOpen, setConfirmModalOpen] = useState(false);
  const [targetUserForStatus, setTargetUserForStatus] = useState(null);

  // Dialog for displaying lists (Districts, Villages, Pincodes)
  const [listDialogOpen, setListDialogOpen] = useState(false);
  const [listDialogTitle, setListDialogTitle] = useState('');
  const [listDialogItems, setListDialogItems] = useState([]);

  useEffect(() => {
    const token = sessionStorage.getItem('token');
    if (token) {
      try {
        const decoded = jwtDecode(token);
        setUserLevel(decoded.level || 1);
        setUserId(decoded.userId || '');
      } catch (err) {
        console.error('Error decoding token:', err);
      }
    }
  }, []);

  useEffect(() => {
    if (userLevel !== null) {
      fetchQPayCentres();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userLevel, userId]);

  const fetchQPayCentres = async () => {
    setLoading(true);
    setError('');
    try {
      const response = await axios.get(`${API_BASE_URL}/users/by-level`, {
        params: {
          level: 4,
          requesterLevel: userLevel,
          userId: userId,
        },
      });
      const data = (Array.isArray(response.data) ? response.data : []).map((u, index) => ({
        ...u,
        id: u._id || index,
      }));
      setUsers(data);
    } catch (err) {
      console.error('Error fetching Q-Pay Centres:', err);
      setError((err && err.response && err.response.data && err.response.data.message) ? err.response.data.message : `Error fetching ${LEVEL_NAMES[4]}s data.`);
      setUsers([]);
    } finally {
      setLoading(false);
    }
  };

  const handleOpenConfirmStatus = (user) => {
    if (!can('users.qpayCentres.changeStatus')) return;
    setTargetUserForStatus(user);
    setConfirmModalOpen(true);
  };

  const handleConfirmStatusToggle = async () => {
    if (!targetUserForStatus) return;
    if (!can('users.qpayCentres.changeStatus')) return;
    const targetId = targetUserForStatus.id || targetUserForStatus._id;
    const currentStatus = targetUserForStatus.status || 'active';
    const newStatus = currentStatus === 'active' ? 'inactive' : 'active';
    const token = sessionStorage.getItem('token');

    try {
      await axios.patch(`${API_BASE_URL}/users/update-status/${targetId}`, {
        status: newStatus,
        requesterId: userId,
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });

      setSnackbarMessage(`${LEVEL_NAMES[4]} ${targetUserForStatus.firstName || ''} ${targetUserForStatus.lastName || ''} ${newStatus === 'active' ? 'activated' : 'deactivated'} successfully`);
      setSnackbarSeverity('success');
      setSnackbarOpen(true);
      fetchQPayCentres();
    } catch (err) {
      console.error('Error updating status:', err);
      const errMsg = (err.response && err.response.data && err.response.data.message) ? err.response.data.message : 'Error updating status';
      setSnackbarMessage(errMsg);
      setSnackbarSeverity('error');
      setSnackbarOpen(true);
    } finally {
      setConfirmModalOpen(false);
      setTargetUserForStatus(null);
    }
  };

  const handleExportCSV = async () => {
    if (!can('users.qpayCentres.export')) return;

    if (!filteredUsers || filteredUsers.length === 0) {
      setSnackbarMessage('No data available to export');
      setSnackbarSeverity('warning');
      setSnackbarOpen(true);
      return;
    }

    const headers = ['SL', `${LEVEL_NAMES[4]} ID`, `${LEVEL_NAMES[3]} ID`, `${LEVEL_NAMES[2]} ID`, 'First Name', 'Last Name', 'Mobile', 'Email', 'Assigned District', 'Assigned Constituency', 'Assigned Pincodes', 'Status'];
    const csvRows = [headers.join(',')];

    filteredUsers.forEach((u, idx) => {
      const sl = idx + 1;
      const qpcId = u.qpayCentreId || u.usercode || (u._id ? `QPC-${String(u._id).slice(-6).toUpperCase()}` : 'N/A');
      const ssId = u.stateStaffId || 'N/A';
      const shId = u.stateHeadId || 'N/A';
      const mobile = u.mobileNumber || u.phoneNumber || '';
      const districts = (u.districts || [u.district || '']).filter(Boolean).join(';');
      const constituencies = (u.villages || [u.city || '']).filter(Boolean).join(';');
      const pincodes = (u.pincodes || [u.pinCode ? String(u.pinCode) : '']).filter(Boolean).join(';');
      
      const row = [
        sl,
        `"${qpcId}"`,
        `"${ssId}"`,
        `"${shId}"`,
        `"${u.firstName || ''}"`,
        `"${u.lastName || ''}"`,
        `"${mobile}"`,
        `"${u.email || ''}"`,
        `"${districts}"`,
        `"${constituencies}"`,
        `"${pincodes}"`,
        `"${u.status || 'active'}"`,
      ];
      csvRows.push(row.join(','));
    });

    const blob = new Blob([csvRows.join('\n')], { type: 'text/csv;charset=utf-8;' });
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.setAttribute('download', `qpay_centres_report_${Date.now()}.csv`);
    document.body.appendChild(link);
    link.click();
    link.remove();
  };

  const handleOpenListDialog = (title, items) => {
    setListDialogTitle(title);
    setListDialogItems(Array.isArray(items) ? items : []);
    setListDialogOpen(true);
  };

  const handleCloseListDialog = () => {
    setListDialogOpen(false);
    setListDialogTitle('');
    setListDialogItems([]);
  };

  const filteredUsers = users.filter((u) => {
    if (filterStatus && (u.status || 'active') !== filterStatus) return false;
    if (!searchText) return true;
    const q = searchText.toLowerCase();
    return (
      (u.qpayCentreId && String(u.qpayCentreId).toLowerCase().includes(q)) ||
      (u.stateHeadId && String(u.stateHeadId).toLowerCase().includes(q)) ||
      (u.stateStaffId && String(u.stateStaffId).toLowerCase().includes(q)) ||
      (u.firstName && u.firstName.toLowerCase().includes(q)) ||
      (u.lastName && u.lastName.toLowerCase().includes(q)) ||
      (u.mobileNumber && u.mobileNumber.includes(searchText)) ||
      (u.phoneNumber && u.phoneNumber.includes(searchText)) ||
      (u.email && u.email.toLowerCase().includes(q)) ||
      (u.address && u.address.toLowerCase().includes(q))
    );
  });

  const columns = [
    {
      field: 'sl',
      headerName: 'SL',
      width: 70,
      sortable: false,
      renderCell: (params) => {
        const index = filteredUsers.findIndex(u => (u.id || u._id) === (params.row.id || params.row._id));
        return (
          <Typography variant="body2" sx={{ fontWeight: 700, color: '#64748b' }}>
            {index >= 0 ? index + 1 : ''}
          </Typography>
        );
      },
    },
    {
      field: 'qpayCentreId',
      headerName: `${LEVEL_NAMES[4]} ID`,
      width: 160,
      renderCell: (params) => (
        <Typography variant="body2" sx={{ fontWeight: 700, color: 'primary.dark' }}>
          {params.value || (params.row._id ? `QPC-${String(params.row._id).slice(-6).toUpperCase()}` : 'N/A')}
        </Typography>
      ),
    },
    {
      field: 'stateStaffId',
      headerName: `${LEVEL_NAMES[3]} ID`,
      width: 150,
      renderCell: (params) => (
        <Typography variant="body2" sx={{ fontWeight: 700, color: '#7c3aed' }}>
          {params.value || 'N/A'}
        </Typography>
      ),
    },
    {
      field: 'stateHeadId',
      headerName: `${LEVEL_NAMES[2]} ID`,
      width: 150,
      renderCell: (params) => (
        <Typography variant="body2" sx={{ fontWeight: 700, color: 'secondary.main' }}>
          {params.value || params.row.stateHeadId || 'N/A'}
        </Typography>
      ),
    },
    {
      field: 'firstName',
      headerName: 'First Name',
      width: 140,
      renderCell: (params) => (
        <Typography variant="body2" sx={{ fontWeight: 600, color: '#0f172a' }}>
          {params.value || 'N/A'}
        </Typography>
      ),
    },
    {
      field: 'lastName',
      headerName: 'Last Name',
      width: 140,
      renderCell: (params) => (
        <Typography variant="body2" sx={{ fontWeight: 600, color: '#334155' }}>
          {params.value || 'N/A'}
        </Typography>
      ),
    },
    {
      field: 'mobileNumber',
      headerName: 'Mobile',
      width: 140,
      renderCell: (params) => (
        <Typography variant="body2" sx={{ fontWeight: 500 }}>
          {params.value || params.row.phoneNumber || 'N/A'}
        </Typography>
      ),
    },
    {
      field: 'email',
      headerName: 'Email',
      width: 190,
      renderCell: (params) => (
        <Typography variant="body2" sx={{ color: '#0369a1', fontWeight: 500 }}>
          {params.value || 'N/A'}
        </Typography>
      ),
    },
    {
      field: 'districts',
      headerName: 'Assigned District',
      width: 160,
      sortable: false,
      renderCell: (params) => {
        const list = params.row.districts || (params.row.district ? [params.row.district] : []);
        return (
          <Button
            size="small"
            variant="outlined"
            color="info"
            startIcon={<LocationCityIcon style={{ fontSize: 14 }} />}
            onClick={() => handleOpenListDialog(`Districts for ${params.row.firstName} ${params.row.lastName || ''}`, list)}
          >
            {list.length > 0 ? `Districts (${list.length})` : 'None'}
          </Button>
        );
      },
    },
    {
      field: 'villages',
      headerName: 'Assigned Constituency',
      width: 170,
      sortable: false,
      renderCell: (params) => {
        const list = params.row.villages || (params.row.city ? [params.row.city] : []);
        return (
          <Button
            size="small"
            variant="outlined"
            color="secondary"
            startIcon={<LocationOnIcon style={{ fontSize: 14 }} />}
            onClick={() => handleOpenListDialog(`Constituencies for ${params.row.firstName} ${params.row.lastName || ''}`, list)}
          >
            {list.length > 0 ? `Constituencies (${list.length})` : 'None'}
          </Button>
        );
      },
    },
    {
      field: 'pincodes',
      headerName: 'Assigned Pincodes',
      width: 170,
      sortable: false,
      renderCell: (params) => {
        const list = params.row.pincodes || (params.row.pinCode ? [String(params.row.pinCode)] : []);
        return (
          <Button
            size="small"
            variant="outlined"
            color="warning"
            startIcon={<PinDropIcon style={{ fontSize: 14 }} />}
            onClick={() => handleOpenListDialog(`Assigned Pincodes for ${params.row.firstName} ${params.row.lastName || ''}`, list)}
          >
            {list.length > 0 ? `Pincodes (${list.length})` : 'None'}
          </Button>
        );
      },
    },
    {
      field: 'status',
      headerName: 'Status',
      width: 160,
      renderCell: (params) => {
        const currentStatus = params.row.status || 'active';
        if (can('users.qpayCentres.changeStatus')) {
          return (
            <Button
              variant="contained"
              size="small"
              color={currentStatus === 'active' ? 'secondary' : 'primary'}
              onClick={() => handleOpenConfirmStatus(params.row)}
            >
              {currentStatus === 'active' ? 'Deactivate' : 'Activate'}
            </Button>
          );
        }
        return (
          <Chip
            label={currentStatus === 'active' ? 'Active' : 'Inactive'}
            color={currentStatus === 'active' ? 'success' : 'error'}
            size="small"
            sx={{ fontWeight: 700 }}
          />
        );
      },
    },
  ];

  return (
    <Box sx={{ p: 3 }}>
      <BackButton />
      <br /><br />

      <Paper elevation={0} sx={{ p: 3, borderRadius: 3, border: '1px solid #e2e8f0' }}>
        <Typography variant="h5" color="primary" sx={{ fontWeight: 800, mb: 0.5 }}>
          {LEVEL_NAMES[4]}s
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
          List of all {LEVEL_NAMES[4]} users and their assigned geographic jurisdictions
        </Typography>

        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        {/* Search, Status Filter & Export CSV */}
        <Grid container spacing={2} alignItems="center" sx={{ mb: 3 }}>
          <Grid item xs={12} md={can('users.qpayCentres.export') ? 5 : 8}>
            <TextField
              fullWidth
              size="small"
              placeholder={`Search by ${LEVEL_NAMES[4]} ID, ${LEVEL_NAMES[2]} ID, Name, Mobile, Email...`}
              value={searchText}
              onChange={(e) => setSearchText(e.target.value)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon color="action" />
                  </InputAdornment>
                ),
                endAdornment: searchText && (
                  <InputAdornment position="end">
                    <IconButton size="small" onClick={() => setSearchText('')}>
                      <ClearIcon />
                    </IconButton>
                  </InputAdornment>
                ),
              }}
            />
          </Grid>
          <Grid item xs={6} md={can('users.qpayCentres.export') ? 3 : 4}>
            <FormControl fullWidth size="small">
              <InputLabel>Status</InputLabel>
              <Select
                value={filterStatus}
                onChange={(e) => setFilterStatus(e.target.value)}
                label="Status"
              >
                <MenuItem value="">All Status</MenuItem>
                <MenuItem value="active">Active</MenuItem>
                <MenuItem value="inactive">Inactive</MenuItem>
              </Select>
            </FormControl>
          </Grid>

          {/* Export CSV Option */}
          {can('users.qpayCentres.export') && (
            <Grid item xs={12} md={4} sx={{ display: 'flex', justifyContent: 'flex-end' }}>
              <Button
                variant="contained"
                color="success"
                startIcon={<DownloadIcon />}
                onClick={handleExportCSV}
                sx={{ fontWeight: 700, height: 40 }}
              >
                Export CSV
              </Button>
            </Grid>
          )}
        </Grid>

        <Box sx={{ height: 500, width: '100%' }}>
          <DataGrid
            rows={filteredUsers}
            columns={columns}
            loading={loading}
            pageSize={10}
            rowsPerPageOptions={[10, 20, 50]}
            disableSelectionOnClick
            rowHeight={60}
            sx={{
              border: 'none',
              '& .MuiDataGrid-columnHeaders': {
                backgroundColor: '#f8fafc',
                fontWeight: 700,
              },
            }}
          />
        </Box>
      </Paper>

      {/* Confirmation Modal for Status Deactivation / Activation */}
      <Dialog
        open={confirmModalOpen}
        onClose={() => setConfirmModalOpen(false)}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle sx={{ fontWeight: 700, color: 'primary.main' }}>
          Confirm Status Change
        </DialogTitle>
        <DialogContent dividers>
          <DialogContentText sx={{ color: '#334155', fontWeight: 500 }}>
            {targetUserForStatus && (
              <>
                Are you sure you want to <strong>{(targetUserForStatus.status || 'active') === 'active' ? 'deactivate' : 'activate'}</strong> {LEVEL_NAMES[4]} <strong>{targetUserForStatus.firstName} {targetUserForStatus.lastName || ''}</strong>?
              </>
            )}
          </DialogContentText>
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <Button onClick={() => setConfirmModalOpen(false)} color="inherit" variant="outlined">
            Cancel
          </Button>
          <Button
            onClick={handleConfirmStatusToggle}
            color={(targetUserForStatus && (targetUserForStatus.status || 'active') === 'active') ? 'error' : 'success'}
            variant="contained"
            sx={{ fontWeight: 700 }}
          >
            Confirm
          </Button>
        </DialogActions>
      </Dialog>

      {/* Dialog for displaying list items */}
      <Dialog open={listDialogOpen} onClose={handleCloseListDialog} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ fontWeight: 700, color: 'primary.main' }}>
          {listDialogTitle}
        </DialogTitle>
        <DialogContent dividers>
          {listDialogItems.length === 0 ? (
            <Typography variant="body2" color="text.secondary">
              No items assigned.
            </Typography>
          ) : (
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, p: 1 }}>
              {listDialogItems.map((item, idx) => (
                <Chip key={idx} label={String(item)} color="primary" variant="outlined" sx={{ fontWeight: 600 }} />
              ))}
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseListDialog} variant="contained" color="primary">
            Close
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={snackbarOpen}
        autoHideDuration={3000}
        onClose={() => setSnackbarOpen(false)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert onClose={() => setSnackbarOpen(false)} severity={snackbarSeverity} sx={{ width: '100%' }}>
          {snackbarMessage}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default QPayCentresList;
